import React, { useEffect, useState } from "react";
import { WifiOff, Wifi } from "lucide-react";

/**
 * Floating pill that signals network status to the nurse.
 *
 * - When offline: persistent amber pill at the top of the viewport so it's
 *   obvious the AI features + PDF generation will fail until reconnection.
 *   Drafts still autosave to localStorage so work isn't lost.
 * - When reconnecting: brief green "back online" pill that auto-dismisses
 *   after 3 seconds.
 */
export default function OfflineBanner() {
  const [online, setOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine
  );
  const [showReconnected, setShowReconnected] = useState(false);

  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      setShowReconnected(true);
      const t = setTimeout(() => setShowReconnected(false), 3000);
      return () => clearTimeout(t);
    };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  if (online && !showReconnected) return null;

  if (online && showReconnected) {
    return (
      <div
        className="fixed top-3 left-1/2 -translate-x-1/2 z-[200] inline-flex items-center gap-2 rounded-full border border-[#B7CADA] bg-[#E7F1FB] px-4 py-1.5 text-[12.5px] font-semibold text-[#0A3D79] shadow-sm"
        data-testid="online-banner"
      >
        <Wifi className="w-3.5 h-3.5" />
        Back online · AI features available
      </div>
    );
  }

  return (
    <div
      className="fixed top-3 left-1/2 -translate-x-1/2 z-[200] inline-flex items-center gap-2 rounded-full border border-[#DBB90A] bg-[#fbf2d8] px-4 py-1.5 text-[12.5px] font-semibold text-[#a07a27] shadow-sm"
      data-testid="offline-banner"
      role="status"
    >
      <WifiOff className="w-3.5 h-3.5" />
      You're offline — drafts are saving locally. Reconnect to run AI or generate PDFs.
    </div>
  );
}
